import styled from 'styled-components';
import { useState, useEffect } from 'react';
import {
  Text3B, Text4B,
} from '../genericComponents/texts';
import { priceFormat, roundPrice } from '../priceFormat';
import RegularPrices from '../models/regularPrices.model';
import Publications from '../models/publications.model';

interface Props{
  // eslint-disable-next-line @typescript-eslint/ban-types
  prices:RegularPrices;
  publications: Publications
  avgTransferences: number
  showTransferencesPrice: boolean
}

export default function MarketPriceRange(props:Props) {
  const {
    prices, publications, avgTransferences, showTransferencesPrice,
  } = props;

  const publicationsList = publications.list || [];
  const minimunPrice = publicationsList.length ? Math.min(...publicationsList.map((publication) => publication.precio || 0)) : 0;
  const maximunPrice = publicationsList.length ? Math.max(...publicationsList.map((publication) => publication.precio || 0)) : 0;
  const averagePublications = publications.price_average || 0;

  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);

  useEffect(() => {
    const allValues = [
      minimunPrice ?? 0,
      maximunPrice ?? 0,
      averagePublications ?? 0,
      avgTransferences ?? 0,
      prices.min_value ?? 0,
      prices.max_value ?? 0,
      prices.autofact_value ?? 0,
    // eslint-disable-next-line no-restricted-globals
    ].filter((value) => typeof value === 'number' && !isNaN(value) && value > 0); // Filtra solo números válidos

    if (allValues.length > 0) {
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);

      setMinValue(min);
      setMaxValue(max);
    } else {
      setMinValue(0);
      setMaxValue(0);
    }
  }, [minimunPrice, maximunPrice, averagePublications, avgTransferences, prices]);

  const range = maxValue - minValue;
  let percentageAvgTransferences = 0;
  let percentageAvgPublications = 0;
  let positionAvgPublications = 0;
  let positionAvgTransferences: number;
  let percentageMinimunPublications = 0;
  let percentageMaximunPublications = 0;

  const calculatePosition = (percentage: number) => Math.max(1, Math.min(9, Math.round(percentage / 10)));

  if (publicationsList.length) {
    const difAvgPublications = averagePublications - minValue;
    percentageAvgPublications = Math.round((difAvgPublications * 100) / range);
    positionAvgPublications = calculatePosition(percentageAvgPublications);

    // calcular la posición del min y max valor de publicaciones
    const difMinimunPublications = minimunPrice - minValue;
    percentageMinimunPublications = Math.round((difMinimunPublications * 100) / range);

    const difMaximunPublications = maximunPrice - minValue;
    percentageMaximunPublications = Math.round((difMaximunPublications * 100) / range);
  }
  if (avgTransferences) {
    const difAvgTransferences = avgTransferences - minValue;
    percentageAvgTransferences = Math.round((difAvgTransferences * 100) / range);
    positionAvgTransferences = calculatePosition(percentageAvgTransferences);

    if (positionAvgPublications && positionAvgPublications === positionAvgTransferences) {
      positionAvgTransferences = positionAvgTransferences > positionAvgPublications
        ? positionAvgTransferences + 1
        : positionAvgTransferences - 1;
    }
  }

  // calcular posición precios recomendados autofact
  const difRecommendedMinValue = prices.min_value ? prices.min_value - minValue : 0;
  const difRecommendedMaxValue = prices.max_value ? prices.max_value - minValue : 0;
  const difRecommendedAfValue = prices.autofact_value ? prices.autofact_value - minValue : 0;

  const percentageRecommendedMinValue = Math.round((difRecommendedMinValue * 100) / range);
  const percentageRecommendedMaxValue = Math.round((difRecommendedMaxValue * 100) / range);
  const percentageRecommendedAfValue = Math.round((difRecommendedAfValue * 100) / range);

  const renderRecommendedBoxes = () => {
    const boxes = [];

    const customDataMap = new Map();

    customDataMap.set(percentageRecommendedMinValue, {
      title: 'Precio recomendado más bajo',
      value: `$${priceFormat(roundPrice(prices.min_value ? prices.min_value : 0, 5))}`,
    });

    customDataMap.set(percentageRecommendedMaxValue, {
      title: 'Precio recomendado más alto',
      value: `$${priceFormat(roundPrice(prices.max_value ? prices.max_value : 0, 5))}`,
    });

    customDataMap.set(percentageRecommendedAfValue, {
      title: 'Precio recomendado Autofact',
      value: `$${priceFormat(roundPrice(prices.autofact_value ? prices.autofact_value : 0, 5))}`,
    });

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i <= 100; i++) {
      // Posiciones intermedias (0 a 10): Verificar datos personalizados
      const customData = customDataMap.get(i);
      if (customData) {
        // Si hay datos personalizados para esta posición
        boxes.push(
          <RecommendedPrice
            key={i}
            title={customData.title}
            value={customData.value}
          />,
        );
      } else {
        boxes.push(<PublicationPrice key={i} title="" value="" />);
      }
    }

    return boxes;
  };

  const renderPriceCasillas = () => {
    const casillas = [];

    const customDataMap = new Map();

    if (publicationsList.length) {
      customDataMap.set(percentageAvgPublications, {
        title: 'Promedio publicaciones',
        value: `$${priceFormat(roundPrice(averagePublications, 5))}`,
      });

      customDataMap.set(percentageMinimunPublications, {
        title: 'Menor precio publicado',
        value: `$${priceFormat(roundPrice(minimunPrice, 5))}`,
      });

      customDataMap.set(percentageMaximunPublications, {
        title: 'Mayor precio publicado',
        value: `$${priceFormat(roundPrice(maximunPrice, 5))}`,
      });
    }

    if (showTransferencesPrice) {
      customDataMap.set(percentageAvgTransferences, {
        title: 'Promedio ventas reales',
        value: `$${priceFormat(roundPrice(avgTransferences, 5))}`,
      });
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i <= 100; i++) {
      // Posiciones intermedias (0 a 10): Verificar datos personalizados
      const customData = customDataMap.get(i);
      if (customData) {
        // Si hay datos personalizados para esta posición
        casillas.push(
          <PublicationPrice
            key={i}
            title={customData.title}
            value={customData.value}
            showIndicator
          />,
        );
      } else {
        casillas.push(<PublicationPrice key={i} title="" value="" />);
      }
    }
    return casillas;
  };

  const isMobile = window.innerWidth <= 700;
  const recommendedBoxes = renderRecommendedBoxes();
  const priceCasillas = renderPriceCasillas();

  return (
    <ContainerParent>
      <Container>
        <SubCont6>
          <SubCont3>
            {isMobile ? recommendedBoxes.slice().reverse() : recommendedBoxes}
          </SubCont3>
          <SubCont1>
            <BarContainer />
          </SubCont1>
          <SubCont3>
            {isMobile ? priceCasillas.slice().reverse() : priceCasillas}
          </SubCont3>
        </SubCont6>
      </Container>
    </ContainerParent>
  );
}

function RecommendedPrice({ title, value }: { title: string, value: string | null }) {
  return (
    <AutoIconTextContainer>
      <TitleValueContainer>
        <TitlePriceRecommended>
          {title}
        </TitlePriceRecommended>
        <BlankSpace width="0px" height="0px" />
        <Text4B>
          {value}
        </Text4B>
        <BlankSpace width="0px" height="0px" />
      </TitleValueContainer>
      <IndicatorTriangleTop />
    </AutoIconTextContainer>
  );
}

function PublicationPrice({
  title, value, showIndicator = false,
}: { title: string, value: string | null, showIndicator?: boolean }) {
  return (
    <AutoIconTextContainer>
      { showIndicator ? <IndicatorTriangleBottom /> : '' }
      <TitleValueContainer>
        <TitlePrice>
          {title}
        </TitlePrice>
        <BlankSpace width="0px" height="0px" />
        <Text3B>
          {value}
        </Text3B>
      </TitleValueContainer>
    </AutoIconTextContainer>
  );
}

const TitleValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-end;
    width: auto;
    margin: 1px 0;
  }
`;

const TitlePrice = styled.div`
  font-size: 10px;
  color: #3D5176;
  text-align: center;
`;

const TitlePriceRecommended = styled.div`
  font-size: 10px;
  color: #3D5176;
  text-align: center;
  font-family: 'Roboto bold';
`;

const Container = styled.div`
  padding: 10px 0;
  display: flex;
  flex-wrap: nowrap;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ContainerParent = styled.div`
  align-items: center;
`;

const SubCont6 = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  @media (max-width: 700px) {
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
    height: 350px;
  }
`;

const SubCont3 = styled.div`
  width: 90%;
  min-width: 250px;
  display: flex;
  flex-direction: row;
  margin: auto;
  justify-content: space-between;
  @media (max-width: 700px) {
    flex-direction: column;
    width: auto;
    min-width: unset;
    height: 100%;
    margin: 0 10px;
    justify-content: flex-end;
    align-items: flex-end;
    flex: 1;
  }
`;

const SubCont1 = styled.div`
  width: 94%;
  min-width: 250px;
  display: flex;
  flex-direction: row;
  margin: auto;
  justify-content: center;
  @media (max-width: 700px) {
    flex-direction: column;
    width: auto;
    min-width: unset;
    margin: 0 10px;
    justify-content: center;
    align-items: center;
    height: 100%;
    flex: 0 0 32px;
    display: flex;
  }
`;

const BarContainer = styled.div`
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 9px;
  font-weight: bold;
  background: linear-gradient(to right, #99B7E8, #1A5ECC);
  border-radius: 10px;
  width: 90%;
  margin: 5px auto;
  @media (max-width: 700px) {
    width: 16px;
    height: 560px;
    min-height: unset;
    background: linear-gradient(to top, #99B7E8, #1A5ECC);
    flex-direction: column;
    border-radius: 10px;
    min-width: 16px;
    max-width: 16px;
  }
`;

const AutoIconTextContainer = styled.div`
  width: 100%;
  margin: 0px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 700px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    width: auto;
    margin: 1px 0;
  }
`;

const IndicatorTriangleTop = styled.div`
  width: 0;
  height: 0;
  margin: auto;
  border-right: 6px solid transparent;
  border-top: 6px solid #14346f;
  border-left: 6px solid transparent;
  @media (max-width: 700px) {
    border-top: none;
    border-left: 6px solid #14346f;
    border-bottom: 6px solid transparent;
    border-right: none;
    border-top: 6px solid transparent;
    margin: 0 0 0 8px;
  }
`;

const IndicatorTriangleBottom = styled.div`
  width: 0;  
  height: 0;
  margin: auto;
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  border-bottom: 8px solid #14346f;
  @media (max-width: 700px) {
    border-bottom: none;
    border-left: 8px solid transparent;
    border-top: 6px solid transparent;
    border-right: 6px solid #14346f;
    border-bottom: 6px solid transparent;
    margin: 0 0 0 8px;
  }
`;

export const BlankSpace = styled.div<{width?:string, height?:string}>`
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '100%'};
`;
